// Generate FS node schema from the saved API workflow.
// Uses isInput flag (classType.startsWith('FSLoad')) to process FSLoad nodes,
// and for FSSave nodes (FS output nodes) only the "filename_prefix" input is added.
// Uses node.inputs.label (if provided) as the label; otherwise, uses node._meta.title (minus "[FS]").
export const generateFSNodeSchema = (
  savedApiWorkflow: any
): { fsInputs: any[]; fsOutputs: any[] } => {
  const fsInputs: any[] = [];
  const fsOutputs: any[] = [];

  if (!savedApiWorkflow) {
    console.log('No saved API workflow provided.');
    return { fsInputs, fsOutputs };
  }

  let jsonContent;
  try {
    jsonContent =
      typeof savedApiWorkflow === 'string' ? JSON.parse(savedApiWorkflow) : savedApiWorkflow;
  } catch (error) {
    console.error('Error parsing savedApiWorkflow:', error);
    return { fsInputs, fsOutputs };
  }

  // For each FS node (nodeId is unique), we generate at most one fsInput entry.
  Object.keys(jsonContent).forEach((nodeId) => {
    const node = jsonContent[nodeId];
    if (!(node._meta && node._meta.title && node._meta.title.includes('[FS]'))) return;

    const classType: string = node.class_type || '';
    const isInput = classType.startsWith('FSLoad');
    const isOutput = classType.startsWith('FSSave');

    // Use node.inputs.label if provided; otherwise, use node._meta.title without "[FS]".
    const rawLabel = node.inputs?.label
      ? String(node.inputs.label) // Force string conversion
      : String(node._meta.title)?.replace('[FS]', '');

    // Improved emoji removal regex that properly captures all emoji characters
    const sanitizedLabel = rawLabel
      ?.replace(
        /(\p{Emoji_Presentation}|\p{Extended_Pictographic}|[\u2600-\u26FF]|[\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|\uD83E[\uDD00-\uDFFF])/gu,
        ''
      )
      ?.trim();

    let representativeEntry: any = null;

    if (node.inputs) {
      if (isInput) {
        let inputKey;
        // Force specific input keys for media types
        switch (classType) {
          case 'FSLoadVideo':
            inputKey = 'video';
            break;
          case 'FSLoadAudio':
            inputKey = 'audio';
            break;
          case 'FSLoadImage':
            inputKey = 'image';
            break;
          case 'FSLoad3D':
            inputKey = '3d';
            break;
          default: {
            // For FSLoad nodes, choose the first key that isn't "label" and isn't "filename_prefix"
            const keys = Object.keys(node.inputs).filter(
              (key) => key !== 'label' && key !== 'filename_prefix'
            );
            inputKey = keys[0];
            break;
          }
        }

        if (inputKey) {
          const parameter_name = `${inputKey}_${Math.floor(Math.random() * 90000) + 10000}`;
          const value = node.inputs?.[inputKey]; // Might be undefined
          const determinedType =
            typeof value === 'number' || inputKey.includes('FSLoadInteger') ? 'integer' : 'string';
          const determinedDemoType =
            inputKey.includes('FSLoadVideo') || classType === 'FSLoadVideo'
              ? 'video'
              : inputKey.includes('FSLoadAudio') || classType === 'FSLoadAudio'
              ? 'audio'
              : inputKey.includes('FSLoadImageFromURL') ||
                inputKey.includes('FSLoadVideoFromURL') ||
                inputKey.includes('FSLoadAudioFromURL') ||
                classType === 'FSLoadImageFromURL' ||
                classType === 'FSLoadVideoFromURL' ||
                classType === 'FSLoadAudioFromURL'
              ? 'text'
              : inputKey.includes('FSLoadImage') || classType === 'FSLoadImage'
              ? 'image'
              : typeof value === 'number' || inputKey.includes('FSLoadInteger')
              ? 'number'
              : inputKey.includes('FSLoad3D') ||
                classType === 'FSLoad3D' ||
                classType === 'FSLoad3d'
              ? '3d'
              : 'text';

          representativeEntry = {
            path: `${nodeId}.inputs.${inputKey}`,
            label: sanitizedLabel,
            parameter_name,
            value_type: determinedType,
            demo_type: determinedDemoType,
            category: 'input',
            randomize: inputKey.includes('seed'),
          };
        }
        // Optionally, if the node has a filename_prefix even on FSLoad,
        // you could choose to add that too. Here we only pick one entry per node.
      } else if (isOutput) {
        // For FSSave nodes, use filename_prefix if available.
        if (node.inputs.hasOwnProperty('filename_prefix')) {
          const parameter_name = `filename_prefix_${Math.floor(Math.random() * 90000) + 10000}`;
          representativeEntry = {
            path: `${nodeId}.inputs.filename_prefix`,
            label: 'filename_prefix',
            parameter_name,
            value_type: 'string',
            demo_type: 'text',
            category: 'output',
            randomize: true,
          };
        }
      }
    }

    if (representativeEntry) {
      // Ensure we add only one entry per nodeId.
      // Check if an entry from this nodeId already exists in fsInputs.
      if (!fsInputs.some((entry) => entry.path.startsWith(`${nodeId}.`))) {
        fsInputs.push(representativeEntry);
      }
    }

    // For FS output nodes, create one output entry per node.
    if (isOutput) {
      let demo_type = 'text';
      if (classType === 'FSSaveImage') {
        demo_type = 'image';
      } else if (classType === 'FSSaveAudio') {
        demo_type = 'audio';
      } else if (classType === 'FSSaveText') {
        demo_type = 'text';
      } else if (classType === 'FSSaveVideo') {
        demo_type = 'video';
      } else if (classType === 'FSSaveInteger') {
        demo_type = 'number';
      } else if (classType === 'FSSave3D' || classType === 'FSSave3d') {
        demo_type = '3d';
      }
      fsOutputs.push({
        path: `${nodeId}.inputs.filename_prefix`,
        label: sanitizedLabel || `output`,
        demo_type,
      });
    }
  });

  return { fsInputs, fsOutputs };
};
